import {
  INVENTORY_CONDITION_REFURBISHED,
  INVENTORY_CONDITION_USED,
  INVENTORY_ITEM_CONDITIONS
} from "constants/inventory-products";
import { HEARING_AID_TYPES, WARRANTY_SUBTYPE } from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import {
  InventoryProduct,
  InventoryProductSpecificationType
} from "services/types/inventory-products.type";

import { InventoryProductSelectedInfo } from "./use-inventory-product-field.hook";

const formatProductOption = (
  inventoryProduct: InventoryProduct,
  name: string,
  getIsInventoryProductSelectedInfo: (
    inventoryProduct: InventoryProduct,
    name: string
  ) => InventoryProductSelectedInfo
): InventoryProduct | null => {
  const { isSelectedById } = getIsInventoryProductSelectedInfo(
    inventoryProduct,
    name
  );
  if (inventoryProduct.id && isSelectedById) {
    return null;
  } else {
    return inventoryProduct;
  }
};

export const formatOptionsWithSelectedInventoryProducts = (
  inventoryProducts: InventoryProduct[],
  name: string,
  getIsProductSelectedInfo: (
    inventoryProduct: InventoryProduct,
    name: string
  ) => InventoryProductSelectedInfo
): InventoryProduct[] => {
  return inventoryProducts
    .map((product) =>
      formatProductOption(product, name, getIsProductSelectedInfo)
    )
    .filter(Boolean) as InventoryProduct[];
};

export const getSpecificationPreviewText = (
  option: InventoryProductSpecificationType
) => {
  const result: string[] = [];
  if (option.specification) {
    const spec = option.specification as any;
    if (spec.ear) result.push(`Ear: ${spec.ear}`);
    if (spec.color) result.push(`Color: ${spec.color}`);
    if (spec.battery) result.push(`Battery: ${spec.battery}`);
    if (spec.material) result.push(`Material: ${spec.material}`);
    if (spec.length || spec.length === 0) result.push(`Length: ${spec.length}`);
    if (spec.gain) result.push(`Gain: ${spec.gain}`);
    if (spec.size || spec.size === 0) result.push(`Size: ${spec.size}`);
    if (spec.type) result.push(`Type: ${spec.type}`);
  } else if (option.product?.master_product?.specification) {
    const { type, subtype } = option.product.master_product;
    const spec = option.product.master_product.specification as any;
    if (spec.type) {
      if (type === HEARING_AID_PRODUCT_TYPE) {
        result.push(`Type: ${HEARING_AID_TYPES.getFromCode(spec.type)}`);
      } else if (type === ACCESSORY_PRODUCT_TYPE) {
        result.push(`Type: ${spec.type}`);
      }
    }
    if (spec.size || spec.size === 0) result.push(`Size: ${spec.size}`);
    if (type === SERVICE_PRODUCT_TYPE && subtype === WARRANTY_SUBTYPE) {
      result.push(`Months: ${spec.warranty_months || 0}`);
    }
  }
  if (option.condition) {
    result.push(
      `Condition: ${INVENTORY_ITEM_CONDITIONS.getFromCode(option.condition)}`
    );
  }
  if (option.specification) {
    const spec = option.specification as any;
    if (spec.additional_notes) {
      result.push(`Notes: ${spec.additional_notes}`);
    }
  }
  return result;
};

export type InventoryPillMeta = {
  serial?: string;
  condition?: string;
  color?: string;
  // Flags a loaner unit so staff can tell it apart from regular clinic stock in
  // the "add from stock" picker (SN-19424).
  loanable?: boolean;
  // Which clinic holds the unit (SPA-6232). Only rendered when the picker's
  // scope spans more than one clinic — see InventoryProductListItem.
  clinic?: { name: string; isCurrent: boolean };
  others: string[];
};

// Group a multi-clinic option list for the Autocomplete: the current clinic's
// units first, other clinics alphabetically, order within a clinic preserved
// (the server's -checked_in_at). MUI's groupBy warns and duplicates headers on
// non-contiguous groups, so the sort is what makes grouping legal.
export const sortInventoryProductsByClinic = (
  inventoryProducts: InventoryProduct[],
  currentClinicId?: number
): InventoryProduct[] => {
  const rank = (product: InventoryProduct) =>
    product.clinic?.id === currentClinicId ? 0 : 1;
  return inventoryProducts
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      const byCurrent = rank(a.product) - rank(b.product);
      if (byCurrent !== 0) return byCurrent;
      const byName = (a.product.clinic?.name ?? "").localeCompare(
        b.product.clinic?.name ?? ""
      );
      if (byName !== 0) return byName;
      return a.index - b.index;
    })
    .map(({ product }) => product);
};

export const getInventoryPillMeta = (
  option: InventoryProduct
): InventoryPillMeta => {
  const serial = option.serial_number || undefined;
  const shouldShowCondition = [
    INVENTORY_CONDITION_REFURBISHED,
    INVENTORY_CONDITION_USED
  ].includes(Number(option.condition));
  const condition = shouldShowCondition
    ? INVENTORY_ITEM_CONDITIONS.getFromCode(Number(option.condition))
    : undefined;
  const spec = option.specification as any;
  const color = spec?.color || undefined;
  const others = getSpecificationPreviewText(
    option as InventoryProductSpecificationType
  ).filter(
    (text) => !text.startsWith("Color:") && !text.startsWith("Condition:")
  );
  return { serial, condition, color, loanable: Boolean(option.loanable), others };
};
